import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getCryptoCheckoutStatus } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bitcoin, Copy, CheckCircle2, Clock, XCircle, Loader2, ArrowLeft, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// QR code via Google Charts API (free, no library dependency)
const qrUrl = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(data)}`;

const STATUS_META = {
  waiting:           { label: 'En attente du paiement',  cls: 'bg-amber-500',    icon: Clock,         color: 'amber' },
  confirming:        { label: 'Confirmation en cours',   cls: 'bg-blue-500',     icon: Loader2,       color: 'blue' },
  confirmed:         { label: 'Confirmé !',              cls: 'bg-emerald-500',  icon: CheckCircle2,  color: 'emerald' },
  sending:           { label: 'Envoi en cours',          cls: 'bg-blue-500',     icon: Loader2,       color: 'blue' },
  partially_paid:    { label: 'Paiement partiel',        cls: 'bg-amber-600',    icon: AlertTriangle, color: 'amber' },
  finished:          { label: 'Paiement validé ✅',       cls: 'bg-emerald-500',  icon: CheckCircle2,  color: 'emerald' },
  paid:              { label: 'Payé ✅',                  cls: 'bg-emerald-500',  icon: CheckCircle2,  color: 'emerald' },
  failed:            { label: 'Échec du paiement',       cls: 'bg-red-500',      icon: XCircle,       color: 'red' },
  refunded:          { label: 'Remboursé',               cls: 'bg-stone-500',    icon: XCircle,       color: 'stone' },
  expired:           { label: 'Expiré',                  cls: 'bg-stone-400',    icon: XCircle,       color: 'stone' },
};

export default function CryptoPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Crypto payment data is passed via location.state OR query params
  const initialData = location.state?.crypto || null;
  const paymentIdFromUrl = searchParams.get('payment_id');

  const [crypto, setCrypto] = useState(initialData);
  const [status, setStatus] = useState({ payment_status: initialData?.payment_status || 'waiting', fulfilled: false });
  const [copied, setCopied] = useState({ address: false, amount: false });
  const [countdown, setCountdown] = useState(null);
  const pollRef = useRef(null);

  // If no data passed via state, fetch by payment_id
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (!crypto && !paymentIdFromUrl) { navigate('/'); return; }
  }, [user, crypto, paymentIdFromUrl, navigate]);

  // Polling status every 8 seconds
  useEffect(() => {
    const pid = crypto?.payment_id || paymentIdFromUrl;
    if (!pid) return;
    if (status.fulfilled) return; // stop polling once fulfilled

    const poll = async () => {
      try {
        const res = await getCryptoCheckoutStatus(pid);
        setStatus(res.data);
        // Sync crypto info if first load via URL
        if (!crypto && res.data) {
          setCrypto({
            payment_id: pid,
            pay_address: res.data.pay_address,
            pay_amount: res.data.pay_amount,
            pay_currency: res.data.pay_currency,
            price_amount: res.data.amount_gbp,
          });
        }
        if (res.data.fulfilled) {
          toast.success('Paiement validé ! Vos tickets ont été crédités.', { duration: 6000 });
          setTimeout(() => navigate('/account'), 2500);
        }
      } catch {
        // silent retry
      }
    };

    poll(); // immediate
    pollRef.current = setInterval(poll, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crypto?.payment_id, paymentIdFromUrl, status.fulfilled]);

  // Countdown to expiration
  useEffect(() => {
    if (!crypto?.expiration_estimate_date) return;
    const exp = new Date(crypto.expiration_estimate_date).getTime();
    const tick = () => {
      const diff = exp - Date.now();
      if (diff <= 0) {
        setCountdown('Expiré');
        return;
      }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${m}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [crypto?.expiration_estimate_date]);

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copié !');
    setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 2000);
  };

  if (!user || !crypto) {
    return <div className="min-h-screen flex items-center justify-center bg-rolex-green"><Loader2 className="w-10 h-10 animate-spin text-gold" /></div>;
  }

  const statusKey = status.payment_status || 'waiting';
  const statusMeta = STATUS_META[statusKey] || STATUS_META.waiting;
  const StatusIcon = statusMeta.icon;
  const isFinal = status.fulfilled || ['finished', 'paid', 'failed', 'expired', 'refunded'].includes(statusKey);
  const isSuccess = status.fulfilled || ['finished', 'paid', 'confirmed'].includes(statusKey);
  const payCurUpper = (crypto.pay_currency || '').toUpperCase();
  const network = crypto.network || (crypto.pay_currency?.includes('trc20') ? 'TRC20 (Tron)' : crypto.pay_currency?.includes('erc20') ? 'ERC20 (Ethereum)' : '');

  // QR data: build payment URI per crypto
  let qrData = crypto.pay_address;
  if (statusKey === 'waiting' && crypto.pay_amount && crypto.pay_currency) {
    const cur = crypto.pay_currency.toLowerCase();
    if (cur === 'btc') qrData = `bitcoin:${crypto.pay_address}?amount=${crypto.pay_amount}`;
    else if (cur === 'eth') qrData = `ethereum:${crypto.pay_address}?value=${crypto.pay_amount}`;
    // Other coins: just the address (most wallets handle it)
  }

  return (
    <div className="min-h-screen bg-rolex-green pt-8 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="gap-2 mb-4 text-cream/65 hover:text-gold hover:bg-transparent"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </Button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/15 border border-gold/40 mb-3">
            <Bitcoin className="w-4 h-4 text-gold" />
            <span className="text-xs uppercase tracking-wider font-bold text-gold">Paiement Crypto</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-cream mb-1">
            £{Number(crypto.price_amount || 0).toFixed(2)}
          </h1>
          <p className="text-cream/65 text-sm">à régler en {payCurUpper}{network && ` (${network})`}</p>
        </div>

        {/* Status banner */}
        <div className={`rounded-2xl ${statusMeta.cls} text-white p-5 mb-5 shadow-xl text-center`}>
          <div className="flex items-center justify-center gap-3 mb-1">
            <StatusIcon className={`w-7 h-7 ${statusKey === 'confirming' || statusKey === 'sending' ? 'animate-spin' : ''}`} />
            <p className="text-xl font-extrabold uppercase tracking-wider">{statusMeta.label}</p>
          </div>
          {countdown && !isFinal && (
            <p className="text-sm opacity-90 mt-1 font-mono">Expire dans {countdown}</p>
          )}
        </div>

        {isSuccess ? (
          /* SUCCESS SCREEN */
          <Card className="bg-emerald-50 border-2 border-emerald-300">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-500 mx-auto mb-4 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} />
              </div>
              <h2 className="font-display text-2xl font-bold text-emerald-900 mb-2">Paiement reçu !</h2>
              <p className="text-emerald-800 text-sm mb-4">
                Vos tickets ont été crédités automatiquement. Un email de confirmation vient de partir.
              </p>
              <Button onClick={() => navigate('/account')} className="btn-gold gap-2">
                Voir mes tickets
              </Button>
            </CardContent>
          </Card>
        ) : statusKey === 'failed' || statusKey === 'expired' || statusKey === 'refunded' ? (
          /* FAILURE SCREEN */
          <Card className="bg-red-50 border-2 border-red-300">
            <CardContent className="p-8 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-display text-2xl font-bold text-red-900 mb-2">Paiement {statusMeta.label.toLowerCase()}</h2>
              <p className="text-red-800 text-sm mb-4">Aucun débit n'a été effectué. Vous pouvez réessayer avec une autre méthode.</p>
              <Button onClick={() => navigate(-1)} className="btn-gold gap-2">
                Réessayer
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* PAYMENT INSTRUCTIONS */
          <Card className="bg-white border-2 border-gold/30 shadow-2xl">
            <CardContent className="p-5 sm:p-6 space-y-5">
              {/* QR Code */}
              <div className="text-center">
                <div className="inline-block p-4 bg-white rounded-2xl border-2 border-gold/20 shadow-md">
                  <img src={qrUrl(qrData)} alt="QR Code paiement" className="w-56 h-56 mx-auto" data-testid="crypto-qr-code" />
                </div>
                <p className="text-xs text-rolex-green/65 mt-2">Scannez avec Trust Wallet, MetaMask, Phantom…</p>
              </div>

              {/* Amount to send */}
              <div className="bg-cream rounded-xl p-4 border border-gold/20">
                <p className="text-[10px] uppercase tracking-wider font-bold text-rolex-green/55 mb-1.5">Montant exact à envoyer</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-2xl font-extrabold text-rolex-green break-all" data-testid="crypto-pay-amount">
                    {crypto.pay_amount} <span className="text-gold-dark">{payCurUpper}</span>
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(String(crypto.pay_amount), 'amount')}
                    className="shrink-0 gap-1.5 border-gold/40 text-gold-dark hover:bg-gold/10"
                    data-testid="crypto-copy-amount"
                  >
                    {copied.amount ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied.amount ? 'Copié' : 'Copier'}
                  </Button>
                </div>
                <p className="text-[10px] text-amber-700 mt-2 italic">⚠️ Envoyez EXACTEMENT ce montant. Tout écart peut empêcher la confirmation.</p>
              </div>

              {/* Address */}
              <div className="bg-cream rounded-xl p-4 border border-gold/20">
                <p className="text-[10px] uppercase tracking-wider font-bold text-rolex-green/55 mb-1.5">Adresse de paiement {network && `· ${network}`}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-xs text-rolex-green break-all bg-white px-3 py-2 rounded border border-gold/15" data-testid="crypto-pay-address">
                    {crypto.pay_address}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(crypto.pay_address, 'address')}
                    className="shrink-0 gap-1.5 border-gold/40 text-gold-dark hover:bg-gold/10"
                    data-testid="crypto-copy-address"
                  >
                    {copied.address ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied.address ? 'Copié' : 'Copier'}
                  </Button>
                </div>
                {network && (
                  <p className="text-[10px] text-red-700 mt-2 italic">⚠️ <strong>Réseau {network} uniquement</strong>. Envoyer depuis un autre réseau = perte définitive des fonds.</p>
                )}
              </div>

              {/* Help / Info */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-xs text-blue-900 font-bold mb-1.5">Comment procéder ?</p>
                <ol className="text-xs text-blue-900/85 space-y-1 list-decimal list-inside">
                  <li>Ouvrez votre wallet (Trust Wallet, MetaMask, Binance...)</li>
                  <li>Scannez le QR code <strong>OU</strong> copiez l'adresse et le montant</li>
                  <li>Envoyez le paiement en {payCurUpper}</li>
                  <li>Cette page se mettra à jour automatiquement (8 sec)</li>
                </ol>
              </div>

              {/* Status indicator */}
              <div className="flex items-center justify-center gap-2 text-xs text-rolex-green/55">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Surveillance du paiement en cours…
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-cream/45 mt-5">
          Powered by <a href="https://nowpayments.io" target="_blank" rel="noopener noreferrer" className="underline hover:text-gold inline-flex items-center gap-0.5">NOWPayments <ExternalLink className="w-3 h-3" /></a>
        </p>
      </div>
    </div>
  );
}
